import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import manageCartwheelSession from '@cartwhl/common/api/manageCartwheelSession';
import { getDashboardUrl, getStudioUrl } from '@cartwhl/common/utils';
import Input from '@cartwhl/ui/atoms/Input';
import Text from '@cartwhl/ui/base/Text';
import GoButton from '@cartwhl/ui/input/GoButton';

import { authStore, noticesStore } from 'stores/global';

import AuthPageLayout from './AuthPageLayout';

export default function LoginTOTPPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Get query parameters
  const params = new URLSearchParams(window.location.search);
  const memberID = params.get('memberID');
  const organizationID = params.get('organizationID');
  const token = params.get('token');
  const qrCode = decodeURIComponent(params.get('qrCode') || '');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const totpCode = e.currentTarget.totpCode.value;

    if (!totpCode || totpCode.length !== 6) {
      noticesStore.error('Please enter a valid 6-digit code');
      return;
    }

    if (!memberID || !organizationID) {
      noticesStore.error('Missing required authentication parameters');
      return;
    }

    setLoading(true);

    const queryParams: Record<string, string> = {
      totpCode,
      memberID,
      organizationID,
    };

    if (token) {
      queryParams.token = token;
    }

    const { data, error } = await manageCartwheelSession('totp', queryParams);

    if (error) {
      noticesStore.error(
        error.message || 'Failed to verify code. Please try again.',
      );
      setLoading(false);
    } else if (data) {
      await authStore.init();
      // Inline redirectOrNavigate logic
      const redirectTo = params.get('redirectTo');
      if (redirectTo === 'studio') {
        window.location.href = getStudioUrl();
      } else if (redirectTo) {
        navigate(`/${redirectTo.replace('/', '')}`);
      } else {
        navigate('/login');
      }
    }
  };

  return (
    <AuthPageLayout
      title="Two-factor authentication"
      subtitle="For security purposes your company requires two-factor authentication via an authenticator app. Please enter the 6-digit code from an authenticator app to complete sign in."
    >
      {qrCode && (
        <div className="mb-6 flex flex-col items-center gap-4">
          <Text variant="studio/body/medium/sm" className="text-primary">
            Scan this QR code with your authenticator app to finish setup.
          </Text>
          <div className="bg-primary border-gray-30 dark:border-gray-70 rounded-lg border p-4">
            <img src={qrCode} alt="TOTP QR Code" className="h-48 w-48" />
          </div>
        </div>
      )}

      <form className="grid gap-x-6 gap-y-6" onSubmit={handleSubmit}>
        <Input
          label="Authentication code"
          type="text"
          name="totpCode"
          placeholder="000000"
          autoComplete="one-time-code"
          maxLength={6}
          pattern="[0-9]{6}"
          inputMode="numeric"
          className="col-span-2"
        />
        <GoButton loading={loading} type="submit">
          Verify
        </GoButton>
      </form>
      <Text variant="studio/body/sm" className="text-tertiary mt-4">
        For issues, email{' '}
        <Link
          to="mailto:pros@getcartwheel.com?subject=Help%20with%20TOTP"
          className="text-primary"
          target="_blank"
        >
          pros@getcartwheel.com
        </Link>{' '}
        for help or reach out to your IT department.
      </Text>
    </AuthPageLayout>
  );
}
